import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import ShareJobModal from "@/components/ShareJobModal";
import CareerTestimonials from "@/components/CareerTestimonials";
import SeoMeta from "@/components/SeoMeta";
import {
    ArrowLeft,
    ArrowRight,
    Buildings,
    MapPin,
    Briefcase,
    UserCircle,
    CurrencyInr,
    Globe,
    Star,
    ShareNetwork,
    Quotes,
    Lightning,
    Clock,
} from "@phosphor-icons/react";

/**
 * White-label public career page for a single client.
 * Feb 2026 — redesigned for applicant conversion:
 *   - Sticky mobile apply bar
 *   - Trust strip (rating + hires + roles) above the fold
 *   - Employee spotlight (top testimonial as hero quote)
 *   - Better job cards with "posted N days ago" + quick-apply CTA
 *   - Cleaner asymmetric hero + narrower content column
 * OG previews still served by /api/share/careers/:slug.
 */
function relativeDays(iso) {
    if (!iso) return "";
    try {
        const days = Math.floor((Date.now() - new Date(iso).getTime()) / (86400 * 1000));
        if (days <= 0) return "Posted today";
        if (days === 1) return "Posted yesterday";
        if (days < 30) return `Posted ${days}d ago`;
        const mo = Math.floor(days / 30);
        return `Posted ${mo}mo ago`;
    } catch { return ""; }
}

export default function PublicCareerPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);
    const [shareJob, setShareJob] = useState(null);

    useEffect(() => {
        let mounted = true;
        api.get(`/public/careers/${slug}`)
            .then((r) => mounted && setData(r.data))
            .catch((e) => mounted && setErr(e.response?.data?.detail || "Careers page not found."));
        return () => { mounted = false; };
    }, [slug]);

    // V1.5.8.49 — document.title is now managed exclusively by <SeoMeta/>
    // (see line 118). The previous direct document.title assignment
    // fought react-helmet-async and left stale titles when navigating
    // between careers pages.

    const copyLink = async () => {
        const url = `${window.location.origin}/api/share/careers/${slug}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (_) { /* ignore */ }
    };

    // Pin the highest-rated (then longest) testimonial as the hero quote.
    const heroTestimonial = useMemo(() => {
        const list = data?.testimonials || [];
        if (!list.length) return null;
        return [...list].sort((a, b) =>
            (b.rating || 0) - (a.rating || 0) ||
            (b.body?.length || 0) - (a.body?.length || 0),
        )[0];
    }, [data]);

    // Show the OTHER testimonials in the section below.
    const otherTestimonials = useMemo(() => {
        const list = data?.testimonials || [];
        if (!heroTestimonial) return list;
        return list.filter((t) => t.id !== heroTestimonial.id);
    }, [data, heroTestimonial]);

    if (err) {
        // V1.5.8.49 — the /api/seo/careers/{slug}/meta endpoint 404s for
        // an unknown client slug, so SeoMeta falls through to `fallback`.
        // Pass an explicit canonical + og image so error routes still
        // present crawlable, richly-unfurlable head tags.
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 text-center">
                <SeoMeta
                    endpoint={`/seo/careers/${slug}/meta`}
                    fallback={{
                        title: "Careers page not found — NaukriDaddy",
                        description: "This careers page isn't available. Browse every open role from Indian hiring agencies on NaukriDaddy.",
                        canonical_url: "https://naukridaddy.com/jobs",
                        og_image_url: "https://naukridaddy.com/og-default.png",
                        robots: "noindex, follow",
                    }}
                />
                <div className="max-w-md">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">Not available</p>
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-ink mb-3">{err}</h1>
                    <Link to="/jobs" className="btn-gradient inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider">
                        Browse all roles <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-[#525252]">Loading…</div>;
    }

    const { client, jobs, job_count, avg_rating } = data;
    const firstJobId = jobs[0]?.id;

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="public-career-page">
            <SeoMeta endpoint={`/seo/careers/${slug}/meta`} />
            {/* Header */}
            <header className="bg-ink text-white sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 shrink-0">
                        <Brand variant="light" size="h-7 sm:h-8" to={null} />
                    </Link>
                    <div className="flex items-center gap-3 text-xs">
                        <Link to="/jobs" className="hidden sm:inline text-white/70 hover:text-white font-bold uppercase tracking-wider">
                            All roles
                        </Link>
                        <button
                            onClick={copyLink}
                            data-testid="careers-share-btn"
                            className="bg-cobalt hover:bg-cobalt-dark px-3 py-1.5 font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5"
                        >
                            <ShareNetwork size={12} weight="bold" />
                            {copied ? "Copied" : "Share"}
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="border-b border-[#E5E5E5] bg-gradient-to-br from-white via-splash/5 to-cobalt/10 relative overflow-hidden">
                {/* Decorative diagonal accent */}
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-splash/10 rotate-12 pointer-events-none" aria-hidden />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14 relative">
                    <Link
                        to="/jobs"
                        className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] hover:text-ink inline-flex items-center gap-1 mb-5"
                    >
                        <ArrowLeft size={12} weight="bold" /> All career pages
                    </Link>

                    <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end">
                        <div className="min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
                                {client.logo_url ? (
                                    <img
                                        src={client.logo_url}
                                        alt={client.name}
                                        data-testid="careers-logo"
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover border border-[#E5E5E5] bg-white shrink-0"
                                    />
                                ) : (
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-ink text-splash flex items-center justify-center shrink-0">
                                        <Buildings size={30} weight="bold" />
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cobalt mb-1 inline-flex items-center gap-1.5">
                                        <Buildings size={11} weight="bold" /> Careers at
                                    </p>
                                    <h1
                                        data-testid="careers-client-name"
                                        className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-ink leading-none"
                                    >
                                        {client.name}
                                    </h1>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#525252] mb-5">
                                {client.industry && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Briefcase size={12} weight="bold" /> {client.industry}
                                    </span>
                                )}
                                {client.location && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={12} weight="bold" /> {client.location}
                                    </span>
                                )}
                                {client.website && (
                                    <a
                                        href={client.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="careers-website-link"
                                        className="inline-flex items-center gap-1.5 text-cobalt hover:underline"
                                    >
                                        <Globe size={12} weight="bold" /> Website
                                    </a>
                                )}
                            </div>

                            {/* Primary CTA */}
                            {firstJobId && (
                                <a
                                    href={`#roles`}
                                    data-testid="careers-hero-cta"
                                    onClick={(e) => { e.preventDefault(); document.getElementById("roles")?.scrollIntoView({ behavior: "smooth" }); }}
                                    className="btn-gradient inline-flex items-center gap-2 px-6 py-3 text-xs font-black uppercase tracking-wider"
                                >
                                    <Lightning size={12} weight="fill" /> See open roles
                                    <ArrowRight size={12} weight="bold" />
                                </a>
                            )}
                        </div>

                        {/* Trust strip */}
                        <div className="lg:min-w-[240px] flex lg:flex-col gap-3 lg:gap-4 border border-[#E5E5E5] bg-white px-4 py-4">
                            <div className="flex-1 lg:flex-none" data-testid="trust-stat-openings">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">Open roles</p>
                                <p className="font-heading text-3xl font-black tracking-tighter text-ink">
                                    <span data-testid="careers-job-count">{job_count}</span>
                                </p>
                            </div>
                            {avg_rating && (
                                <div className="flex-1 lg:flex-none border-l lg:border-l-0 lg:border-t border-[#E5E5E5] pl-3 lg:pl-0 lg:pt-3" data-testid="trust-stat-rating">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">Rating</p>
                                    <p className="font-heading text-3xl font-black tracking-tighter text-ink inline-flex items-center gap-1">
                                        {avg_rating}
                                        <Star size={16} weight="fill" className="text-[#F59E0B]" />
                                    </p>
                                    <p className="text-[10px] text-[#A3A3A3]">from placed candidates</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Employee spotlight (pinned testimonial) */}
            {heroTestimonial && (
                <section className="border-b border-[#E5E5E5] bg-ink text-white" data-testid="careers-hero-testimonial">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-8 items-start">
                        <div className="w-14 h-14 bg-splash text-ink flex items-center justify-center shrink-0">
                            <Quotes size={22} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2">
                                What placed candidates say
                            </p>
                            <blockquote className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-snug mb-4">
                                “{heroTestimonial.body}”
                            </blockquote>
                            <div className="flex items-center gap-3 text-xs text-white/70">
                                <div>
                                    <p className="font-bold text-white">
                                        {heroTestimonial.anonymous ? "Placed candidate" : heroTestimonial.candidate_name || "Placed candidate"}
                                    </p>
                                    {heroTestimonial.job_title && (
                                        <p className="text-white/50">Hired as {heroTestimonial.job_title}</p>
                                    )}
                                </div>
                                {heroTestimonial.rating && (
                                    <div className="flex items-center gap-0.5 ml-auto">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                size={14}
                                                weight="fill"
                                                className={n <= heroTestimonial.rating ? "text-[#F59E0B]" : "text-white/20"}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Jobs list */}
            <section id="roles" className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="flex items-baseline justify-between gap-4 mb-6">
                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">Open roles</p>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                            {job_count === 0 ? "Currently between hires" : `${job_count} way${job_count === 1 ? "" : "s"} to join`}
                        </h2>
                    </div>
                </div>

                {jobs.length === 0 ? (
                    <div className="bg-white border border-[#E5E5E5] p-10 text-center" data-testid="careers-empty">
                        <p className="font-heading text-2xl font-bold tracking-tight text-ink mb-2">
                            No open roles right now.
                        </p>
                        <p className="text-sm text-[#525252] mb-5">
                            Check back soon or explore other agencies hiring on NaukriDaddy.
                        </p>
                        <Link to="/jobs" className="btn-gradient inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider">
                            Browse all roles <ArrowRight size={12} weight="bold" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {jobs.map((j) => (
                            <article
                                key={j.id}
                                data-testid={`careers-job-${j.id}`}
                                className="bg-white border border-[#E5E5E5] p-5 sm:p-6 hover:border-ink transition-colors group"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            {j.created_at && (
                                                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] inline-flex items-center gap-1">
                                                    <Clock size={9} weight="bold" /> {relativeDays(j.created_at)}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-ink group-hover:text-cobalt transition-colors">
                                            {j.title}
                                        </h3>
                                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#525252]">
                                            {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="bold" /> {j.location}</span>}
                                            {j.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={11} weight="bold" /> {j.employment_type}</span>}
                                            {j.seniority && <span className="inline-flex items-center gap-1"><UserCircle size={11} weight="bold" /> {j.seniority}</span>}
                                            {j.salary_range && <span className="inline-flex items-center gap-1 font-bold text-ink"><CurrencyInr size={11} weight="bold" /> {j.salary_range}</span>}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            onClick={() => setShareJob({ id: j.id, title: j.title })}
                                            data-testid={`careers-share-job-${j.id}`}
                                            aria-label="Share role"
                                            className="border border-[#E5E5E5] hover:border-ink p-2 transition-colors bg-white"
                                        >
                                            <ShareNetwork size={12} weight="bold" />
                                        </button>
                                        <button
                                            onClick={() => navigate(`/jobs/${j.id}`)}
                                            data-testid={`careers-apply-${j.id}`}
                                            className="btn-gradient px-4 py-2 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                                        >
                                            Apply <ArrowRight size={11} weight="bold" />
                                        </button>
                                    </div>
                                </div>
                                {j.snippet && (
                                    <p className="text-sm text-[#525252] line-clamp-2">{j.snippet}</p>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </section>

            {/* Remaining testimonials as a compact grid */}
            {otherTestimonials.length > 0 && (
                <CareerTestimonials items={otherTestimonials} avgRating={avg_rating} />
            )}

            {/* Sticky mobile apply bar */}
            {firstJobId && (
                <div
                    data-testid="careers-mobile-apply-bar"
                    className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E5E5] px-4 py-3 flex items-center gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
                >
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                            {job_count} open role{job_count === 1 ? "" : "s"}
                        </p>
                        <p className="text-xs font-bold text-ink truncate">at {client.name}</p>
                    </div>
                    <button
                        onClick={() => document.getElementById("roles")?.scrollIntoView({ behavior: "smooth" })}
                        className="btn-gradient px-4 py-2.5 text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 shrink-0"
                    >
                        Apply <ArrowRight size={11} weight="bold" />
                    </button>
                </div>
            )}

            <footer className="border-t border-[#E5E5E5] bg-white py-6 pb-[calc(theme(spacing.6)+env(safe-area-inset-bottom))] sm:pb-6">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
                    <p className="text-[11px] text-[#A3A3A3]">
                        Powered by <Link to="/" className="text-ink font-bold hover:underline">NaukriDaddy</Link> — India&apos;s smartest agency ATS.
                    </p>
                </div>
            </footer>

            <ShareJobModal
                open={!!shareJob}
                onClose={() => setShareJob(null)}
                jobId={shareJob?.id}
                jobTitle={shareJob?.title}
                clientName={client.name}
            />
        </div>
    );
}
